import React from "react";
import {Box, Typography} from "@mui/material";

import {BarChartPro, BarPlot, ChartsAxis, ChartsAxisHighlight, ChartsDataProvider, ChartsGrid,
        ChartsLegend, ChartsOverlay, ChartsSurface, ChartsTooltip, ChartsWrapper, FunnelChart,
        Gauge, Heatmap, LineChartPro, LinePlot, MarkPlot, PieChartPro, RadarChart, SankeyChart,
        ScatterChartPro, SparkLineChart} from "@mui/x-charts-pro";
import {Blue2, Blue3, Blue4, Grey6} from "../../theme/styles";
import {toNumber} from "./chartShape";
import {ChartSpec, axisOf, axisSettingsOf, categoryAxisProps, drawnAs, gridProps, indexOfSlot,
        indexesOfSlot, legendPosition, refuse, seriesSettingsOf, singleDrawing, usesSecondaryAxis,
        valueAxisProps} from "./chartSpec";
import {ResultColumn} from "./reportApi";

/**
 * A report result, drawn from a SPECIFICATION.
 *
 * This file is now only a compiler: specification in, MUI element out. Everything about what a
 * chart NEEDS lives in chartSpec's capability table, which is also what the builder reads -- so a
 * chart that can be built is a chart that can be drawn, by construction rather than by two lists
 * kept in step by hand.
 *
 * @mui/x-charts-pro DIRECTLY, AND NOT THE HOUSE CCBarChart -- which was tried first, and is the
 * wrong tool for this one case.
 *
 * Every other chart in this suite is EC-BACKED: CCChart's componentDidMount registers fifteen
 * listeners on `this.props.applet.getEnterpriseComponent()`, so the component IS the data source
 * and the chart redraws when it loads. Rendered without an applet it throws
 * "Cannot read properties of undefined (reading 'getEnterpriseComponent')" -- which is exactly what
 * it did, and what the Playwright suite caught the first time anybody pressed Bar.
 *
 * A report chart has no EnterpriseComponent behind it. Its rows are the ones the STORE kept, held
 * as plain arrays on the page, and wrapping them in an EC to satisfy a constructor would be
 * inventing a model the data does not have.
 *
 * A REFUSAL IS RENDERED, NOT AN EMPTY RECTANGLE. Switching to a chart a result cannot support is
 * the classic way to get a blank panel and no explanation; here the reason is on screen.
 *
 * HEIGHT IS A NUMBER. MUI v9 charts take a number, and a string silently produces nothing -- which
 * is indistinguishable from the data being wrong.
 */

type Props = {
    spec: ChartSpec;
    columns: ResultColumn[];
    rows: unknown[][];
    height?: number;
};

/**
 * The heat scale: ONE HUE, LIGHT TO DARK, from the suite's own blues.
 *
 * SEQUENTIAL MEANS ONE HUE. Colour is carrying MAGNITUDE here, not identity, and a rainbow scale
 * makes a reader learn an arbitrary order before they can read the picture -- green is not obviously
 * more than orange. Light-to-dark needs no legend to be understood, and the legend then says how
 * much rather than which.
 *
 * NOT STARTED FROM THE PALEST BLUE. Blue1 measures 1.7:1 against a white card, and a lone
 * low-valued cell in a sparse grid would be a square the reader cannot see -- indistinguishable
 * from a cell with no row at all, which is a real and different thing here. Blue2 is 2.1:1 and
 * Blue4 5.1:1, so both ends sit on the card and the span between them is monotone in lightness.
 */
const HEAT: readonly [string, string] = [Blue2, Blue4];

/**
 * That same scale cut into `count` steps, for a chart whose parts have an ORDER.
 *
 * ORDERED, NOT IDENTIFIED. A funnel's stages are a sequence -- swapping two of them changes what
 * the picture says -- so they take one hue deepening through the run, and the reader sees the order
 * in the colour without reading a legend. Giving each stage a hue of its own would spend the
 * identity channel re-encoding what the widths already show, and would imply the stages are
 * unrelated things.
 *
 * INTERPOLATED IN sRGB, which is what MUI does between the two ends of a continuous colour map --
 * so a funnel of four stages and a heatmap of the same spread are the same colours, rather than
 * nearly.
 */
function rampOf(count: number): string[] {
    const ends = HEAT.map(hex => [1, 3, 5].map(at => parseInt(hex.slice(at, at + 2), 16)));
    if (count < 2) return [HEAT[0]];
    return Array.from({length: count}, (_one, index) => {
        const t = index / (count - 1);
        const mixed = ends[0].map((from, channel) =>
            Math.round(from + (ends[1][channel] - from) * t));
        return `#${mixed.map(one => one.toString(16).padStart(2, "0")).join("")}`;
    });
}

/** A column's values down the rows, as numbers. An unparseable one is a GAP, never a zero. */
function numbersAt(rows: unknown[][], at: number): (number | null)[] {
    return at < 0 ? [] : rows.map(row => toNumber(row[at]));
}

/** Room for the longest category label, within reason.
 *
 *  MEASURED GENEROUSLY. MUI reserves part of the axis width for the tick marks and then TRUNCATES
 *  the label to what is left, so a formula that is merely close produces "RELE…" -- which was the
 *  first version of this, and looked like a rendering quirk rather than the chart failing to say
 *  what it is showing. Capped so one long department cannot take half the drawing. */
function axisWidth(categories: string[]): number {
    const longest = categories.reduce((most, one) => Math.max(most, one.length), 0);
    return Math.min(240, Math.max(80, longest * 9 + 24));
}

/** The distinct values of a column, in the order the rows first show them.
 *
 *  THE ORDER IS THE RESULT'S, not sorted. A report the reader ordered by month must not have its
 *  axis alphabetised into April, August, December -- the ordering is a choice they already made on
 *  the report, and re-making it here would silently overrule them. */
function distinctAt(rows: unknown[][], at: number): string[] {
    const order: string[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
        const value = String(row[at] ?? "");
        if (!seen.has(value)) { seen.add(value); order.push(value); }
    }
    return order;
}

function labelsAt(rows: unknown[][], at: number): string[] {
    return at < 0 ? rows.map((_row, index) => String(index + 1))
                  : rows.map(row => String(row[at] ?? ""));
}

export default function ReportChart({spec, columns, rows, height = 340}: Props) {
    const refusal = refuse(spec, columns, rows);
    if (refusal) {
        return (
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "center",
                      height, border: "1px solid #E4E7EC", borderRadius: "4px"}}>
                <Typography sx={{fontSize: 13, color: Grey6}}>
                    This report {refusal.why}.
                </Typography>
            </Box>
        );
    }

    const options = spec.options ?? {};
    const hideLegend = !!options.hideLegend;

    if (spec.kind === "gauge") {
        // ONE NUMBER. `refuse` has already established a single row and a column on the slot.
        const value = numbersAt(rows, indexOfSlot(spec, columns, "values"))[0] ?? 0;
        return <Gauge height={height} value={value} text={({value: shown}) => `${shown ?? ""}`}/>;
    }

    if (spec.kind === "pie") {
        const at = indexOfSlot(spec, columns, "values");
        const labels = labelsAt(rows, indexOfSlot(spec, columns, "category"));
        const values = numbersAt(rows, at);
        return <PieChartPro height={height} hideLegend={hideLegend} series={[{
            data: labels.map((label, index) => ({id: index, value: values[index] ?? 0, label})),
            // A DONUT IS A PIE WITH A HOLE, which is all the option means -- so it is one number
            // here rather than a second chart kind in the table.
            innerRadius: options.donut ? 60 : 0,
        }]}/>;
    }

    if (spec.kind === "scatter") {
        const xAt = indexOfSlot(spec, columns, "x");
        const yAt = indexOfSlot(spec, columns, "y");
        const labels = labelsAt(rows, indexOfSlot(spec, columns, "label"));
        const xs = numbersAt(rows, xAt);
        const ys = numbersAt(rows, yAt);

        // A POINT NEEDS BOTH NUMBERS. A row missing either is dropped rather than plotted at zero,
        // for the same reason an unparseable value is a gap: nought is a claim about the data.
        const data = xs
            .map((x, index) => ({id: index, x, y: ys[index], label: labels[index]}))
            .filter(one => one.x !== null && one.y !== null)
            .map(one => ({id: one.id, x: one.x as number, y: one.y as number, label: one.label}));

        return <ScatterChartPro
            height={height}
            hideLegend={hideLegend}
            xAxis={[{label: columns[xAt]?.Label}]}
            // WIDE ENOUGH FOR ITS OWN LABELS, as the bar and line value axes already are. A
            // scatter's y axis carries the same big numbers and had the same 45px to draw them
            // in: "10,000,000" rendered as "10,00…", unreadable on the axis that gives every
            // point its meaning.
            yAxis={[{label: columns[yAt]?.Label, width: "auto"}]}
            series={[{
                data,
                label: `${columns[yAt]?.Label ?? ""} by ${columns[xAt]?.Label ?? ""}`,
            }]}/>;
    }

    if (spec.kind === "sparkline") {
        // NO AXES, NO LEGEND, NO GRID -- that is what makes it a sparkline rather than a small line
        // chart. `refuse` has established two rows, so there is a shape to draw.
        const at = indexOfSlot(spec, columns, "values");
        const labels = labelsAt(rows, indexOfSlot(spec, columns, "category"));
        // A GAP WOULD BREAK THE TYPE, which takes number[]. Reading it as nought is the one thing
        // this file never does, so an unreadable value drops its point and the line closes over it
        // -- the same shape a line chart draws through a gap.
        const values = numbersAt(rows, at).filter(one => one !== null) as number[];

        // SCALED TO ITS OWN RANGE, NOT TO ZERO -- and this is what makes it a sparkline rather than
        // a line chart with the furniture taken off. A sparkline carries no axis, so it makes no
        // claim about magnitude; what it shows is SHAPE. Left on MUI's zero baseline, three review
        // periods of 19.13, 19.05 and 18.81 draw as a flat line across the top of a mostly empty
        // box -- a chart that is technically correct and says nothing. Padded by a twentieth of the
        // range so the extremes are not drawn flat against the edges.
        const low = Math.min(...values);
        const high = Math.max(...values);
        const pad = (high - low || Math.abs(high) || 1) / 20;

        return <SparkLineChart
            // SMALL, WHATEVER THE PAGE ALLOWS. Every other chart here fills the height it is given;
            // a sparkline is a shape meant to sit inside something else, and at 340px it stops
            // being one -- the fill becomes a block of colour with a wobble along the top.
            height={Math.min(height, 84)}
            data={values}
            plotType={options.shape === "bar" ? "bar" : "line"}
            area={options.shape === "bar" ? undefined : !!options.area}
            // FILLED FROM THE BOTTOM OF ITS OWN RANGE, for the same reason: filling from zero on a
            // scale that does not start at zero draws an area whose size means nothing.
            baseline={options.shape === "bar" ? undefined : "min"}
            showTooltip
            showHighlight
            // ONE SERIES, SO ONE COLOUR, and the app's primary rather than a ramp: there is no
            // magnitude to encode across marks, only the shape of the line.
            color={Blue3}
            yAxis={{min: low - pad, max: high + pad}}
            xAxis={{scaleType: "band", data: labels.slice(0, values.length)}}
            valueFormatter={(value) => `${value ?? ""}`}/>;
    }

    if (spec.kind === "radar") {
        // THE ROWS ARE THE AXES, the transpose of every other chart here: a spoke per row, a shape
        // per measure. `refuse` has established three or more rows and no unreadable value, so
        // every shape closes.
        const metrics = labelsAt(rows, indexOfSlot(spec, columns, "category"));
        const series = indexesOfSlot(spec, columns, "values").map(at => ({
            label: columns[at]?.Label,
            data: numbersAt(rows, at).map(one => one ?? 0),
            fillArea: !!options.fillArea,
            hideMark: !!options.hideMark,
        }));

        // ONE SCALE FOR EVERY SPOKE, and without this the chart is meaningless. MUI gives each
        // metric its OWN max by default, so every vertex lands on the outer ring whatever the
        // numbers are: Acme's own DRAFT 3, RELEASED 2, VOID 4 drew as an equilateral triangle
        // touching the edge three times. A radar's whole claim is that the SHAPE is the answer --
        // a dent means less -- and per-axis scaling removes exactly that.
        //
        // FROM ZERO, because a radar's centre reads as nothing and a floor above zero turns a
        // small difference into a dramatic one. Found by drawing a real tenant's rows; five
        // invented departments with two measures had hidden it, because varied numbers on
        // independent scales still produce a lopsided shape that looks plausible.
        const highest = Math.max(...series.flatMap(one => one.data), 0);

        return <RadarChart height={height} hideLegend={hideLegend}
                           // Rounded up, so the outermost ring is a round number rather than the
                           // largest value sitting exactly on the edge.
                           radar={{metrics, max: highest > 0 ? Math.ceil(highest * 1.05) : 1}}
                           series={series}/>;
    }

    if (spec.kind === "funnel") {
        const at = indexOfSlot(spec, columns, "values");
        const labels = labelsAt(rows, indexOfSlot(spec, columns, "category"));
        const values = numbersAt(rows, at);
        const shades = rampOf(rows.length);

        return <FunnelChart
            height={height}
            hideLegend={hideLegend}
            series={[{
                // THE ORDER SENT IS THE ORDER SHOWN, and it is the reader's: the rows arrive in
                // whatever order the report was sorted into. Re-sorting by value here would make
                // every funnel descend, including the ones whose whole point is that they do not.
                data: labels.map((label, index) => ({
                    id: index, value: values[index] ?? 0, label, color: shades[index],
                })),
                layout: options.horizontal ? "horizontal" : "vertical",
                curve: (options.shape as any) || "linear",
            }]}/>;
    }

    if (spec.kind === "sankey") {
        const fromAt = indexOfSlot(spec, columns, "x");
        const toAt = indexOfSlot(spec, columns, "y");
        const at = indexOfSlot(spec, columns, "values");
        const values = numbersAt(rows, at);

        // ONE LINK PER ROW. The nodes are left to the library: it takes them from the ids the
        // links name, so a node list here would be a second copy of the same information and a
        // chance for the two to disagree.
        //
        // A ROW WITHOUT A NUMBER IS DROPPED, not drawn at nought -- a flow of no width is not a
        // flow, and nought is a claim the data did not make. `refuse` has already established
        // there are no loops, no cycles and nothing negative.
        const links = rows
            .map((row, index) => ({source: String(row[fromAt] ?? ""),
                                   target: String(row[toAt] ?? ""),
                                   value: values[index]}))
            .filter(one => one.value !== null && one.source && one.target)
            .map(one => ({...one, value: one.value as number}));

        return <SankeyChart
            height={height}
            series={{
                data: {links},
                nodeOptions: {showLabels: options.showLabels !== false,
                              align: (options.align as any) || "justify"},
            }}/>;
    }

    if (spec.kind === "heatmap") {
        const xAt = indexOfSlot(spec, columns, "x");
        const yAt = indexOfSlot(spec, columns, "y");
        const at = indexOfSlot(spec, columns, "values");

        // THE AXES ARE THE DISTINCT VALUES and the cells are addressed by INDEX into them -- which
        // is the shape MUI's heatmap takes: [column, row, value]. `refuse` has already established
        // that no two rows share a cell, so the grid cannot quietly lose one here.
        const across = distinctAt(rows, xAt);
        const down = distinctAt(rows, yAt);
        const values = numbersAt(rows, at);

        const data = rows
            .map((row, index) => [across.indexOf(String(row[xAt] ?? "")),
                                  down.indexOf(String(row[yAt] ?? "")),
                                  values[index]] as const)
            // A CELL WITH NO NUMBER IS LEFT OUT, not coloured as nought. Nought is the palest end
            // of the scale and would read as a real, low value -- the same reason a gap is a gap
            // everywhere else here.
            .filter(one => one[2] !== null)
            .map(one => [one[0], one[1], one[2] as number] as [number, number, number]);

        // THE SCALE COMES FROM THE DATA, AND THIS IS THE ONE THING THAT MUST NOT BE LEFT DEFAULT.
        // MUI's heatmap falls back to a colour map of min 0, max 100 -- a hard-coded range, not one
        // measured from the series. Counts of 0..7 all land in the palest band and a result in the
        // thousands all lands in the darkest: a uniform block, indistinguishable from the query
        // being wrong. Measured here, so the palette spans what is actually there.
        const numbers = data.map(one => one[2]);
        const low = Math.min(...numbers);
        const high = Math.max(...numbers);

        return <Heatmap
            height={height}
            hideLegend={hideLegend}
            // The room for the row labels is reserved by the margin, the mechanism that works on a
            // banded axis in this library -- see the note on the bar chart below.
            margin={{left: axisWidth(down)}}
            xAxis={[{data: across}]}
            yAxis={[{data: down, width: "auto"}]}
            // EVERY CELL THE SAME COLOUR IS A FLAT RESULT, NOT A BROKEN CHART. One distinct value
            // makes low === high, and a zero-width range colours nothing; widened by one so the
            // single value sits at the bottom of the scale and the legend still reads.
            //
            // THE COLOUR MAP IS SUPPLIED, NOT JUST THE RANGE -- and this is the trap. Passing a
            // zAxis WITHOUT one does not inherit the library's default palette: it leaves the cells
            // with no colour scale at all, and the plot returns null for every cell it cannot
            // colour. The chart draws its axes, its legend and NOTHING ELSE, which looks exactly
            // like a result with no rows. The default is only reached by passing no zAxis, which is
            // the thing we cannot do -- see the range note above.
            zAxis={[{min: low, max: low === high ? low + 1 : high,
                     colorMap: {type: "continuous", min: low,
                                max: low === high ? low + 1 : high, color: HEAT}}]}
            series={[{data, label: columns[at]?.Label ?? ""}]}/>;
    }

    const categories = labelsAt(rows, indexOfSlot(spec, columns, "category"));
    const valueIndexes = indexesOfSlot(spec, columns, "values");
    const valueKeys = valueIndexes.map(at => columns[at]?.Key ?? String(at));

    // THE VALUE AXIS IS X WHEN THE BARS LIE DOWN. `layout="horizontal"` swaps which axis carries
    // the numbers, so the id a series is tied to has to swap with it -- a series pinned to
    // `yAxisId` on a horizontal chart is pinned to the CATEGORY axis, which MUI resolves by
    // ignoring it and drawing everything on one scale. That is the whole feature failing silently
    // for anybody who pressed "Lay it on its side".
    const sideways = spec.kind === "bar" && !!options.horizontal;
    const axisIdProp = sideways ? "xAxisId" : "yAxisId";

    const stacked = spec.kind === "bar" && !!options.stack;
    const stackMode = (options.stackMode as string) || "none";

    // ONE DRAWING FOR ALL OF THEM, OR A COMBINATION. `null` means the measures are not all drawn
    // the same way, which is the only case the ready-made chart components cannot express.
    const only = singleDrawing(spec, valueKeys, sideways);

    const series = valueIndexes.map((at, index) => {
        const key = valueKeys[index];
        const own = seriesSettingsOf(spec, key);
        const drawing = drawnAs(spec, key, sideways);
        return {
            // THE TYPE TRAVELS WITH THE SERIES. The ready-made components take it from themselves
            // and ignore it; the composed path below cannot work without it, and sending it in
            // both cases keeps one object rather than two that can drift.
            type: drawing,
            label: columns[at]?.Label,
            data: numbersAt(rows, at),
            [axisIdProp]: axisOf(spec, key),
            ...(own.color ? {color: own.color} : {}),
            // STACKING IS A BAR'S. A line given a stack becomes a stacked area, which is a
            // different chart from the one the reader asked for -- so a line in a combination
            // stays a line over whatever the bars beneath it add up to.
            ...(stacked && drawing === "bar"
                ? {stack: "total", ...(stackMode !== "none" ? {stackOffset: stackMode} : {})}
                : {}),
            ...(drawing === "line" ? {
                // THE CHART'S LINE SETTINGS APPLY TO WHATEVER IS DRAWN AS A LINE, including a
                // line living on a bar chart -- otherwise a combination's line would be the only
                // line in the app with no shape, no marks and no choice about gaps. A bar chart
                // has no line controls of its own, so those read as their defaults there and the
                // per-series ones are what the reader actually turns.
                // PER SERIES WHEN IT SAYS SO, OTHERWISE THE CHART'S. `??` rather than `||`
                // throughout, because a series that has deliberately turned something OFF is
                // `false` and must not fall through to a chart that has it on.
                //
                // A BAR CHART HAS NO LINE OPTIONS OF ITS OWN, so a line living on one falls back
                // to `false` rather than to a control that is not on the screen.
                area: own.area ?? (spec.kind === "line" ? !!options.area : false),
                curve: own.curve || (options.curve as string) || "monotone",
                showMark: own.showMark ?? !!options.showMark,
                connectNulls: own.connectNulls
                              ?? (spec.kind === "line" ? !!options.connectNulls : false),
            } : {}),
        };
    });

    // ONE AXIS PER SCALE, and a second only when a series asked for it. Each gets its tick step
    // from ITS OWN series rather than from all of them: whole-number orders beside fractional
    // rates should not make the rates step by one.
    const onAxis = (which: "primary" | "secondary") =>
        series.filter((_one, index) => axisOf(spec, valueKeys[index]) === which);

    const axisFor = (which: "primary" | "secondary") => ({
        id: which,
        position: sideways ? (which === "primary" ? "bottom" : "top")
                           : (which === "primary" ? "left" : "right"),
        // WIDE ENOUGH FOR ITS OWN LABELS. The default is 45px, or 65 with a title, and "2,500,000"
        // does not fit either -- it renders as "2,500,0…", which is a number the reader cannot
        // read on the axis that gives every bar its meaning. `auto` measures the ticks.
        //
        // ONLY WHEN THE VALUE AXIS IS THE Y AXIS: `width` is a y-axis property, and on a chart
        // laid on its side the numbers are along the bottom, where height is what would be
        // reserved. MUI's own types say so -- `never` on an x axis.
        ...(sideways ? {} : {width: "auto" as const}),
        ...valueAxisProps(axisSettingsOf(spec, which), wholeNumbers(onAxis(which))),
    });

    const valueAxes: any[] = [axisFor("primary")];
    if (usesSecondaryAxis(spec, valueKeys)) valueAxes.push(axisFor("secondary"));

    // scaleType "band" is what makes the axis categorical rather than numeric; without it a chart
    // of five departments plots them at 0..4 and labels them as numbers.
    //
    // AND IT SIZES ITSELF TO ITS OWN LABELS, which is the mirror of the value axis's `width`. An x
    // axis reserves 25px by default -- 45 with a title -- and a label turned Upright needs far
    // more than that, so every value was cut off at the ankles. `auto` measures them.
    //
    // ONLY WHEN THE CATEGORIES ARE ON X: `height` is an x-axis property, `never` on a y axis in
    // MUI's own types. Laid on its side the categories move to y, where the room is reserved by
    // the chart MARGIN instead -- see the note on that below, which was learned the hard way.
    const bandAxis: any = {scaleType: "band", data: categories,
                           ...(sideways ? {} : {height: "auto" as const}),
                           ...categoryAxisProps(axisSettingsOf(spec, "category"))};

    const frame = {
        grid: gridProps(options.grid),
        slotProps: {legend: {position: legendPosition(options.legendPosition)}},
    };

    // A COMBINATION: bars and a line on the same axes.
    //
    // HAND-COMPOSED, BECAUSE NO READY-MADE COMPONENT DRAWS ONE. `BarChartPro` renders a `BarPlot`
    // and `LineChartPro` a `LinePlot`; neither renders the other, whatever `type` the series
    // carries. MUI's own composition layer is the supported way through -- this is `LineChart`'s
    // own structure with `BarPlot` added, which is why the order below matters: bars first so the
    // line is drawn over them, the axes after the plots so the axis lines are not painted on, and
    // the marks in their own group as `LineChart` does.
    //
    // ONLY FOR THE COMBINATION. Everything above is computed once and shared, so the two paths
    // cannot disagree about an axis, a colour or a stack -- they differ in the final element
    // alone, and a chart that IS all bars or all lines keeps the component it has always used.
    if (only === null) {
        return (
            <ChartsDataProvider height={height} series={series as any}
                                xAxis={[bandAxis]} yAxis={valueAxes}>
                <ChartsWrapper hideLegend={hideLegend}
                               legendPosition={legendPosition(options.legendPosition)}>
                    {hideLegend ? null : <ChartsLegend/>}
                    <ChartsSurface>
                        {frame.grid ? <ChartsGrid {...frame.grid}/> : null}
                        <BarPlot/>
                        <LinePlot/>
                        <ChartsOverlay/>
                        <ChartsAxisHighlight x="band"/>
                        <ChartsAxis/>
                        <g data-drawing-container><MarkPlot/></g>
                    </ChartsSurface>
                    <ChartsTooltip/>
                </ChartsWrapper>
            </ChartsDataProvider>
        );
    }

    // NOT `spec.kind`: a bar chart whose every measure was set to a line IS a line chart, and
    // belongs on the component that draws one rather than on the composed path above.
    if (only === "line") {
        return <LineChartPro height={height} hideLegend={hideLegend}
                             {...frame}
                             xAxis={[bandAxis]}
                             yAxis={valueAxes}
                             series={series as any}/>;
    }

    return <BarChartPro height={height} hideLegend={hideLegend}
                        layout={options.horizontal ? "horizontal" : "vertical"}
                        {...frame}
                        // THE MARGIN, NOT THE AXIS WIDTH, IS WHAT RESERVES THE ROOM. Setting
                        // `width` on the band y-axis was the obvious reading and MUI ignored it
                        // here -- the labels stayed truncated at "RELE…" through three attempts.
                        // The chart margin is the mechanism that works, and this is written down
                        // because the obvious one looks like it should.
                        margin={options.horizontal
                            ? {left: axisWidth(categories)} : undefined}
                        // GAPS ARE THE BANDED AXIS'S, not the series'. MUI reads them off the
                        // categorical axis, which is the one that owns the band being divided up.
                        {...(options.horizontal
                            ? {yAxis: [{...bandAxis, ...barGaps(options)}], xAxis: valueAxes}
                            : {xAxis: [{...bandAxis, ...barGaps(options)}], yAxis: valueAxes})}
                        series={series as any}/>;
}

/** Whether every value on these series is a whole number.
 *
 *  A COUNT OF THREE DOES NOT HAVE TENTHS. MUI chooses its tick count from the available width, and
 *  a wide chart of small integers gets 0.0, 0.1, 0.2 ... which is forty labels saying nothing and
 *  implying a precision the data does not have. `valueAxisProps` turns this into `tickMinStep`. */
function wholeNumbers(series: {data: (number | null)[]}[]): boolean {
    const values = series.flatMap(one => one.data).filter(one => one !== null) as number[];
    return values.length > 0 && values.every(one => Number.isInteger(one));
}

/** The two bar gaps, passed only when the reader moved them off the library's own defaults. */
function barGaps(options: Record<string, any>): Record<string, number> {
    const out: Record<string, number> = {};
    if (typeof options.categoryGap === "number") out.categoryGapRatio = options.categoryGap;
    if (typeof options.barGap === "number") out.barGapRatio = options.barGap;
    return out;
}
